import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isIDNotSet, StringID } from "userful-chronos-app-common-js/dist/models/common";
import { RESTRICTED_USERS, SSHUser, SSHUserCreate } from "userful-chronos-app-common-js/dist/models/config/sshUsers";
import { requestAddOrUpdateSSHUser, requestListSSHUsers } from "./sshUsersMsgSenders";

const DEFAULT_SSH_USERNAME = "userful";

const buildDefaultUser = (id: StringID): SSHUserCreate => ({
    id,
    username: DEFAULT_SSH_USERNAME,
    publicKeys: [],
    userfulKeys: true,
});

const isDefaultUserForId = (user: SSHUser, id: StringID): boolean =>
    user.username === DEFAULT_SSH_USERNAME && user.id?.value === id.value;

const findDefaultUser = (users: SSHUser[], id: StringID): SSHUser | undefined =>
    users.find((user) => isDefaultUserForId(user, id));

const filterRestrictedUsers = (users: SSHUser[]): SSHUser[] =>
    users.filter((user) => !RESTRICTED_USERS.includes(user.username));

const ensureDefaultUserExists = (state: { sshUsers: SSHUser[] }, id: StringID) => {
    if (isIDNotSet(id)) {
        return;
    }
    const existingUserful = findDefaultUser(state.sshUsers, id);
    if (existingUserful) {
        if (!existingUserful.userfulKeys) {
            requestAddOrUpdateSSHUser(buildDefaultUser(id));
        }
        return;
    }
    requestAddOrUpdateSSHUser(buildDefaultUser(id));
};

type PendingEnsure = StringID | null;

const initialState: {
    sshUsers: SSHUser[];
    ready: boolean;
    pendingEnsure: PendingEnsure;
} = {
    sshUsers: [],
    ready: false,
    pendingEnsure: null,
};

export const sshUsersSlice = createSlice({
    name: "sshUsersSlice",
    initialState,
    reducers: {
        setData: (state, action: PayloadAction<SSHUser[]>) => {
            state.sshUsers = filterRestrictedUsers(action.payload);
            state.ready = true;
            if (state.pendingEnsure) {
                ensureDefaultUserExists(state, state.pendingEnsure);
                state.pendingEnsure = null;
            }
        },
        addOrUpdateData: (state, action: PayloadAction<SSHUser>) => {
            if (RESTRICTED_USERS.includes(action.payload.username)) {
                return;
            }
            const updateIDValue = action.payload.username;
            const existingIndex = state.sshUsers.findIndex(
                (item) => item.username === updateIDValue && item.id.value === action.payload.id.value,
            );
            if (existingIndex < 0) {
                state.sshUsers = [...state.sshUsers, { ...action.payload }];
            } else {
                state.sshUsers = [
                    ...state.sshUsers.slice(0, existingIndex),
                    { ...action.payload },
                    ...state.sshUsers.slice(existingIndex + 1),
                ];
            }
        },
        requestList: (state, action: PayloadAction<StringID>) => {
            if (isIDNotSet(action.payload)) {
                return;
            }
            state.ready = false;
            requestListSSHUsers(action.payload);
        },
        ensureDefaultUser: (state, action: PayloadAction<StringID>) => {
            if (isIDNotSet(action.payload)) {
                return;
            }
            if (!state.ready) {
                if (!state.pendingEnsure || state.pendingEnsure.value !== action.payload.value) {
                    state.pendingEnsure = action.payload;
                    requestListSSHUsers(action.payload);
                }
                return;
            }
            ensureDefaultUserExists(state, action.payload);
        },
    },
});

export const sshUsersActions = sshUsersSlice.actions;

export default sshUsersSlice.reducer;
